import { makeAutoObservable, observable, runInAction } from "mobx";
import type { RootStore } from "./root.store";
import type { ReportLookupItem } from "@/types/report";

/**
 * One fully-materialized `{ id, name }` lookup list (project *or* request),
 * loaded on demand and cached per workspace slug. `ensure` is idempotent (a
 * no-op once loaded for the current slug, deduped while in flight); `reload`
 * always re-fetches (the picker's retry). A failure records `error` and logs it
 * — never swallowed — so the filter row can surface a retry.
 */
class LookupSlot {
  items: ReportLookupItem[] = [];
  error: Error | null = null;
  private loaded = false;
  private inFlight: Promise<void> | null = null;
  private slug: string | null = null;

  constructor(
    private readonly fetcher: (slug: string) => Promise<ReportLookupItem[]>,
    private readonly label: string,
  ) {
    makeAutoObservable(this, { items: observable.ref, fetcher: false, label: false } as never, {
      autoBind: true,
    });
  }

  ensure(slug: string): Promise<void> {
    if (!slug) return Promise.resolve();
    if (this.slug === slug && this.loaded) return Promise.resolve();
    if (this.slug === slug && this.inFlight) return this.inFlight;
    return this.reload(slug);
  }

  reload(slug: string): Promise<void> {
    if (!slug) return Promise.resolve();
    this.slug = slug;
    this.error = null;
    const promise = this.fetcher(slug)
      .then((items) =>
        runInAction(() => {
          this.items = items;
          this.loaded = true;
        }),
      )
      .catch((err: unknown) => {
        const error = err instanceof Error ? err : new Error(String(err));
        runInAction(() => {
          this.error = error;
        });
        console.error(`[report-lookup] failed to load ${this.label} lookups`, { slug, error });
      })
      .finally(() =>
        runInAction(() => {
          if (this.inFlight === promise) this.inFlight = null;
        }),
      );
    this.inFlight = promise;
    return promise;
  }
}

/**
 * Backs the report builder's project + request relation-filter pickers (#3504).
 * The workspace project / request stores are cursor-paginated (they hold only
 * the first page), so a picker over them would silently omit entities past page
 * one — a latent "why can't I filter by X" bug. These dedicated lookups return
 * the whole visible `{ id, name }` set in one shot.
 */
export class ReportLookupStore {
  readonly projects: LookupSlot;
  readonly requests: LookupSlot;

  constructor(rootStore: RootStore) {
    this.projects = new LookupSlot(
      (slug) => rootStore.reportService.listProjectLookups(slug),
      "project",
    );
    this.requests = new LookupSlot(
      (slug) => rootStore.reportService.listRequestLookups(slug),
      "request",
    );
  }
}
